import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  BarChart2,
  BarChart3,
  Clock3,
  Database,
  Download,
  Filter,
  Globe,
  LayoutDashboard,
  LineChart,
  Mic,
  Palette,
  PieChart,
  Plus,
  RefreshCcw,
  RotateCcw,
  Settings2,
  Table2,
  Trash2,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Bubble, Doughnut, Line, Scatter } from "react-chartjs-2";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { getSavedDashboard, generateDashboard, refreshSavedDashboard } from "../../apiDashboard";
import { getLlmProviderErrorMessage } from "../../utils/llmProviderError";
import { useTheme } from "../../context/ThemeContext";
import EmptyState from "../ui/EmptyState";
import SkeletonLoader from "../ui/SkeletonLoader";
import { toast } from "react-toastify";
import { Button } from "../ui/Button.tsx";
import { Card } from "../ui/Card.tsx";
import { Textarea } from "../ui/Textarea.tsx";
import { buildDashboardLayoutPlan } from "./dashboardLayoutPlanner";
import { DEMO_DASHBOARD_ID, isDemoDashboardId } from "./dashboardDemo";
import { readScopedValue, writeScopedValue } from "../../utils/scopedStorage";
import "./DashboardAgent.css";
import ExportModal from "./Exportmodal";
import "./Exportmodal.css";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler, zoomPlugin, ChartDataLabels
);

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCENT_PALETTE = [
  { color: "#3B82F6", soft: "rgba(59,130,246,0.12)", glow: "rgba(59,130,246,0.22)", bg: "linear-gradient(135deg,#3B82F6,#60A5FA)" },
  { color: "#8B5CF6", soft: "rgba(139,92,246,0.12)", glow: "rgba(139,92,246,0.22)", bg: "linear-gradient(135deg,#8B5CF6,#A78BFA)" },
  { color: "#10B981", soft: "rgba(16,185,129,0.12)", glow: "rgba(16,185,129,0.22)", bg: "linear-gradient(135deg,#10B981,#34D399)" },
  { color: "#F59E0B", soft: "rgba(245,158,11,0.12)", glow: "rgba(245,158,11,0.22)", bg: "linear-gradient(135deg,#F59E0B,#FBBF24)" },
  { color: "#EF4444", soft: "rgba(239,68,68,0.12)",  glow: "rgba(239,68,68,0.22)",  bg: "linear-gradient(135deg,#EF4444,#F87171)" },
  { color: "#06B6D4", soft: "rgba(6,182,212,0.12)",  glow: "rgba(6,182,212,0.22)",  bg: "linear-gradient(135deg,#06B6D4,#22D3EE)" },
];

const VOICE_LANGUAGES = [
  { code: "en-IN", label: "English (India)", shortLabel: "EN (India)" },
  { code: "en-US", label: "English (US)", shortLabel: "EN (US)" },
  { code: "hi-IN", label: "Hindi", shortLabel: "HI" },
  { code: "mr-IN", label: "Marathi", shortLabel: "MR" },
  { code: "gu-IN", label: "Gujarati", shortLabel: "GU" },
  { code: "ta-IN", label: "Tamil", shortLabel: "TA" },
  { code: "te-IN", label: "Telugu", shortLabel: "TE" },
  { code: "kn-IN", label: "Kannada", shortLabel: "KN" },
  { code: "ml-IN", label: "Malayalam", shortLabel: "ML" },
];

// Smart chart-type compatibility based on expected shape
const COMPATIBLE_CHART_TYPES = {
  time_series:      [{ value: "line",     label: "Line",    Icon: LineChart  }, { value: "bar", label: "Bar", Icon: BarChart3 }],
  category_ranking: [{ value: "bar",      label: "Bar",     Icon: BarChart3  }, { value: "doughnut", label: "Pie", Icon: PieChart }],
  comparison:       [{ value: "bar",      label: "Bar",     Icon: BarChart3  }, { value: "line", label: "Line", Icon: LineChart }],
  distribution:     [{ value: "doughnut", label: "Pie",     Icon: PieChart   }, { value: "bar", label: "Bar", Icon: BarChart3 }],
  default:          [{ value: "line",     label: "Line",    Icon: LineChart  }, { value: "bar", label: "Bar", Icon: BarChart3 }, { value: "doughnut", label: "Pie", Icon: PieChart }],
};

const DEFAULT_EDIT_STATE = {
  chartTypeOverride: null,
  colorOverrides: null,
  editedLabels: null,
  editedValues: null,
  showDataLabels: false,
};

const SERIES_COLORS = [
  "#3B82F6",
  "#EF4444",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#06B6D4",
  "#F97316",
  "#14B8A6",
  "#EC4899",
  "#84CC16",
  "#6366F1",
  "#EAB308",
  "#0EA5E9",
  "#22C55E",
  "#DC2626",
  "#A855F7",
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const hslToHex = (h, s, l) => {
  const hue = ((h % 360) + 360) % 360;
  const sat = clamp(s, 0, 100) / 100;
  const light = clamp(l, 0, 100) / 100;

  const chroma = (1 - Math.abs(2 * light - 1)) * sat;
  const segment = hue / 60;
  const x = chroma * (1 - Math.abs((segment % 2) - 1));

  let r = 0;
  let g = 0;
  let b = 0;

  if (segment >= 0 && segment < 1) [r, g, b] = [chroma, x, 0];
  else if (segment < 2) [r, g, b] = [x, chroma, 0];
  else if (segment < 3) [r, g, b] = [0, chroma, x];
  else if (segment < 4) [r, g, b] = [0, x, chroma];
  else if (segment < 5) [r, g, b] = [x, 0, chroma];
  else [r, g, b] = [chroma, 0, x];

  const match = light - chroma / 2;
  const toHex = (channel) => Math.round((channel + match) * 255).toString(16).padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const getIndexedSeriesColor = (index) => {
  if (SERIES_COLORS[index]) return SERIES_COLORS[index];

  const offset = index - SERIES_COLORS.length;
  const hue = 221 + offset * 137.508;
  const saturation = 72 - ((offset % 3) * 6);
  const lightness = 50 + ((offset % 2) * 8);
  return hslToHex(hue, saturation, lightness);
};

const KPI_WIDGET_TYPES = new Set(["kpi", "kpi_with_trend", "kpi_card"]);

const widgetIconByShape = {
  single_value: TrendingUp, metric_with_trend: TrendingUp,
  time_series: LineChart,   category_ranking: BarChart3,
  comparison: BarChart2,    distribution: PieChart,
  detail_table: Table2,
};

// ─── Utility helpers ──────────────────────────────────────────────────────────

const readCssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const fmt = (value, fallback = "--") =>
  value === null || value === undefined || value === "" ? fallback : String(value);

const formatTimestamp = (value) => {
  if (!value) return "Just now";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const formatRelativeTime = (value) => {
  if (!value) return "Just now";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 10) return "Just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const parseDateOnly = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null;
  const [y, m, d] = String(value).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const fmtMonth = (d) => d.toLocaleString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });

const formatTimeframeBadge = ({ timeframeLabel, timeframeStart, timeframeEnd }) => {
  const s = parseDateOnly(timeframeStart), e = parseDateOnly(timeframeEnd);
  if (s && e) {
    const isFY = s.getUTCMonth() === 3 && s.getUTCDate() === 1 && e.getUTCMonth() === 2 && e.getUTCDate() === 31 && e.getUTCFullYear() === s.getUTCFullYear() + 1;
    if (isFY) return `FY${String(s.getUTCFullYear()).slice(-2)}-${String(e.getUTCFullYear()).slice(-2)}`;
    return `${fmtMonth(s)} – ${fmtMonth(e)}`;
  }
  return String(timeframeLabel || "").trim();
};

const normalizeSavedDashboard = (sd) => ({
  agent_type: "dashboard", dashboard_response_id: `saved_${sd.id}`,
  source_db_config_id: sd.source_db_config_id, question: sd.question,
  dashboard_plan: sd.dashboard_plan, review: sd.review,
  planning_context_summary: sd.planning_context_summary,
  dashboard: sd.dashboard, saved_dashboard_id: sd.id,
  created_at: sd.created_at, updated_at: sd.updated_at,
});

function hasGrowthIndicator(w) {
  return w?.indicator_direction !== undefined || w?.indicator_sentiment !== undefined ||
    w?.indicator_style_key !== undefined || w?.delta_percent !== undefined ||
    w?.comparison_display_value !== undefined || w?.comparison_value !== undefined;
}

const isKpiWidget = (w) =>
  KPI_WIDGET_TYPES.has(String(w?.widget_type || "")) ||
  w?.expected_shape === "metric_with_trend" || w?.expected_shape === "single_value" ||
  hasGrowthIndicator(w);

const isDetailTableWidget = (w) =>
  w?.expected_shape === "detail_table" || String(w?.widget_type || "").includes("table");

const hasUsableChartData = (w) => {
  const cd = w?.chart_data;
  if (!cd || typeof cd !== "object") return false;
  if (cd?.series && typeof cd.series === "object" && !Array.isArray(cd.series) && Object.keys(cd.series).length) return true;
  if (Array.isArray(cd?.datasets) && cd.datasets.some((d) => Array.isArray(d?.data) && d.data.length)) return true;
  if (Array.isArray(cd?.labels) && cd.labels.length && Array.isArray(cd?.values) && cd.values.length) return true;
  return false;
};

const normalizeWidgetForDisplay = (w) => {
  if (!w) return null;
  if (isKpiWidget(w)) return w;
  if (isDetailTableWidget(w)) return Array.isArray(w?.table_data) && w.table_data.length ? w : null;
  return hasUsableChartData(w) ? w : null;
};

const normalizeDashboardForDisplay = (dashboard) => {
  if (!dashboard) return null;
  const widgets = (Array.isArray(dashboard?.widgets) ? dashboard.widgets : []).map(normalizeWidgetForDisplay).filter(Boolean);
  const visibleIds = new Set(widgets.map((w) => w.widget_id));
  const layout = (Array.isArray(dashboard?.layout) ? dashboard.layout : [])
    .map((s) => ({ ...s, widget_ids: (s?.widget_ids || []).filter((id) => visibleIds.has(id)) }))
    .filter((s) => s.widget_ids.length);
  return { ...dashboard, layout, widgets };
};

function hasSparklineData(w) {
  return (Array.isArray(w?.trend_series) && w.trend_series.length > 1) ||
    (Array.isArray(w?.chart_data?.values) && w.chart_data.values.length > 1);
}

function extractSparklineValues(w) {
  if (Array.isArray(w?.trend_series) && w.trend_series.length > 1) {
    if (typeof w.trend_series[0] === "number") return w.trend_series;
    const v = w.trend_series.map((p) => Number(p?.value)).filter((v) => Number.isFinite(v));
    if (v.length > 1) return v;
  }
  if (Array.isArray(w?.chart_data?.values) && w.chart_data.values.length > 1) return w.chart_data.values;
  return [];
}

const getDeltaLabel = (dp) => {
  if (dp === null || dp === undefined || dp === "") return null;
  const n = String(dp).trim(); if (!n) return null;
  if (/%$/.test(n)) return /^[+-]/.test(n) ? n : `+${n}`;
  return /^-/.test(n) ? `${n}%` : `+${n}%`;
};

const getIndicatorMeta = (w) => {
  const dir = w?.indicator_direction || "flat";
  const sentiment = w?.indicator_sentiment || "neutral";
  const cv = w?.comparison_display_value ?? w?.comparison_value ?? null;
  const cp = String(w?.comparison_label_short || "").trim();
  const prefix = cp ? (/^vs\b/i.test(cp) ? cp : `vs ${cp}`) : "vs prev period";
  const dl = getDeltaLabel(w?.delta_percent);
  if (dir === "flat" || !dl) return { className: "is-neutral", isUp: null, prefix, compVal: cv === null ? null : fmt(cv), dl: null };
  const className = sentiment === "positive" ? "is-positive" : sentiment === "negative" ? "is-negative" : "is-neutral";
  return { className, isUp: dir === "up", prefix, compVal: cv === null ? null : fmt(cv), dl };
};

// ─── CSV download helper ──────────────────────────────────────────────────────

const downloadTableCSV = (tableData, title) => {
  if (!tableData?.length) return;
  const headers = Object.keys(tableData[0]);
  const rows = tableData.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","));
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `${title || "table"}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ─── Export helpers ───────────────────────────────────────────────────────────

// const exportDashboard = (format, displayDashboard, metadata) => {
//   if (!displayDashboard) { toast.warn("No dashboard to export."); return; }
//   const payload = {
//     format,
//     title: metadata?.title || "Dashboard",
//     generated_at: metadata?.generated_at || new Date().toISOString(),
//     widgets: (displayDashboard.widgets || []).map((w) => ({
//       widget_id: w.widget_id, title: w.title,
//       type: w.expected_shape || w.widget_type,
//       chart_data: w.chart_data || null,
//       table_data: w.table_data || null,
//       value: w.display_value ?? w.value ?? null,
//     })),
//   };
//   const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement("a"); a.href = url;
//   a.download = `dashboard_${format}_export_${Date.now()}.json`; a.click();
//   URL.revokeObjectURL(url);
//   toast.success(`Exported for ${format === "powerbi" ? "Power BI" : "Tableau"}`);
// };

// ─── Sparkline ────────────────────────────────────────────────────────────────

const Sparkline = ({ values, color }) => {
  const sv = Array.isArray(values) && values.length > 1 ? values : [0, 0];
  const max = Math.max(...sv), min = Math.min(...sv), span = max - min || 1;
  const pts = sv.map((v, i) => `${(i / (sv.length - 1)) * 100},${32 - ((v - min) / span) * 26}`).join(" ");
  return (
    <svg viewBox="0 0 100 34" className="da-sparkline" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`sg-${color.replace(/[^a-z0-9]/gi, "")}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,34 ${pts} 100,34`} fill={`url(#sg-${color.replace(/[^a-z0-9]/gi, "")})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard = ({ widget, accent, planItem }) => {
  const ind = getIndicatorMeta(widget);
  const showInd = hasGrowthIndicator(widget);
  const showSpark = hasSparklineData(widget);
  const Icon = widgetIconByShape[widget.expected_shape] || TrendingUp;

  return (
    <div
      className="da-kpi-card"
      style={{
        "--ka": accent.color, "--ka-soft": accent.soft, "--ka-glow": accent.glow,
        "--ka-bg": accent.bg, "--kspan": planItem.span, "--kspan-md": planItem.spanMd,
      }}
    >
      <div className="da-kpi-card__top">
        <span className="da-kpi-card__label">{widget.title}</span>
        <span className="da-kpi-card__icon-wrap"><Icon size={16} /></span>
      </div>
      <div className="da-kpi-card__value">{fmt(widget?.display_value ?? widget?.value)}</div>
      {showInd && ind.dl && (
        <div className={`da-kpi-card__badge ${ind.className}`}>
          {ind.isUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          <span>{ind.dl}</span>
          <span className="da-kpi-card__badge-prefix">{ind.prefix}</span>
        </div>
      )}
      {showSpark && <Sparkline values={extractSparklineValues(widget)} color={accent.color} />}
    </div>
  );
};

// ─── Chart helpers ────────────────────────────────────────────────────────────

const getSeriesColor = (i, palette) => palette.series?.[i] || getIndexedSeriesColor(i);

const getAreaFill = (color) => (ctx) => {
  const chart = ctx?.chart, ca = chart?.chartArea;
  if (!ca) return `${color}33`;
  const g = chart.ctx.createLinearGradient(0, ca.top, 0, ca.bottom);
  g.addColorStop(0, `${color}88`); g.addColorStop(1, `${color}05`);
  return g;
};

// ─── DashboardChart ───────────────────────────────────────────────────────────

const DashboardChart = ({ widget, palette, theme, chartTypeOverride, colorOverrides, editedLabels, editedValues, showDataLabels, onResetRef }) => {
  const chartRef = useRef(null);
  useEffect(() => { if (onResetRef) onResetRef(() => chartRef.current?.resetZoom?.()); }, [onResetRef]);
  const cdi = useMemo(() => widget?.chart_data || {}, [widget]);
  const meta = useMemo(() => cdi?.meta || {}, [cdi]);

  const normalizedChartType = useMemo(() => {
    if (chartTypeOverride) return chartTypeOverride;
    if (cdi?.chart_type === "histogram") return "bar";
    if (cdi?.chart_type === "pie") return "doughnut";
    return cdi?.chart_type || "line";
  }, [chartTypeOverride, cdi]);

  const isDoughnut = normalizedChartType === "doughnut" || (!chartTypeOverride && widget?.expected_shape === "distribution");
  const isScatter = normalizedChartType === "scatter";
  const isBubble = normalizedChartType === "bubble";
  const isArea = meta?.is_area === true;
  const isStacked = meta?.is_stacked === true || meta?.stacked === true;
  const isDualAxis = meta?.is_dual_axis === true;
  const secondaryKeys = useMemo(() => meta?.secondary_series_keys || [], [meta]);

  const seriesObj = cdi?.series && typeof cdi.series === "object" && !Array.isArray(cdi.series) ? cdi.series : null;
  const rawDs = Array.isArray(cdi?.datasets) && cdi.datasets.length ? cdi.datasets : null;
  const labels = useMemo(() => editedLabels || cdi?.labels || [], [editedLabels, cdi]);
  const values = useMemo(() => editedValues || cdi?.values || [], [editedValues, cdi]);
  const dsLabel = meta?.chart_title || widget?.title;

  const resolveColor = useCallback((i) => colorOverrides?.[i] || getSeriesColor(i, palette), [colorOverrides, palette]);

  const chartData = useMemo(() => {
    if (isDoughnut) {
      const sliceColors = labels.map((_, i) => colorOverrides?.[i] || getSeriesColor(i, palette));
      return {
        labels,
        datasets: [{
          data: values,
          borderWidth: 2,
          borderColor: labels.map(() => palette.surface),
          hoverBorderColor: labels.map(() => palette.surface),
          hoverBorderWidth: 3,
          backgroundColor: sliceColors.map((color) => `${color}DD`),
          hoverOffset: 8,
        }],
      };
    }
    if (isBubble && seriesObj?.Data) {
      return { datasets: [{ label: meta?.y_label || dsLabel, data: seriesObj.Data, backgroundColor: `${resolveColor(0)}80`, borderColor: resolveColor(0), borderWidth: 1.5 }] };
    }
    if (isScatter && seriesObj) {
      return { datasets: Object.entries(seriesObj).map(([lbl, pts], i) => { const c = resolveColor(i); return { label: lbl, data: pts || [], borderColor: c, backgroundColor: c, pointRadius: 4, pointHoverRadius: 7, showLine: false }; }) };
    }
    if (seriesObj) {
      return {
        labels,
        datasets: Object.entries(seriesObj).map(([lbl, sv], i) => {
          const c = resolveColor(i); const sec = isDualAxis && secondaryKeys.includes(lbl); const asLine = normalizedChartType === "line" || sec;
          return { label: lbl, data: sv || [], type: asLine ? "line" : "bar", yAxisID: sec ? "y1" : "y", order: sec ? 0 : 1, borderColor: c, backgroundColor: asLine && isArea && !sec ? getAreaFill(c) : asLine ? c : `${c}CC`, borderWidth: asLine ? 2.2 : 0, fill: asLine && isArea && !sec ? "origin" : false, tension: asLine ? 0.38 : 0, pointRadius: asLine ? 3 : 0, pointHoverRadius: asLine ? 6 : 0, pointBackgroundColor: "#fff", pointBorderColor: c, pointBorderWidth: asLine ? 2 : 0, borderRadius: asLine ? undefined : 6, borderSkipped: asLine ? undefined : false, maxBarThickness: 28, stack: isStacked ? "s0" : undefined };
        }),
      };
    }
    if (rawDs) {
      return {
        labels,
        datasets: rawDs.map((ds, i) => {
          const dt = ds.type || (normalizedChartType === "line" ? "line" : "bar"); const c = colorOverrides?.[i] || ds.borderColor || ds.backgroundColor || getSeriesColor(i, palette);
          if (dt === "line") return { ...ds, type: "line", label: ds.label || `${dsLabel} ${i + 1}`, data: ds.data || [], borderColor: c, backgroundColor: ds.fill || isArea ? getAreaFill(c) : c, fill: ds.fill ?? (isArea ? "origin" : false), tension: ds.tension ?? 0.36, pointRadius: ds.pointRadius ?? 3, pointHoverRadius: 6, borderWidth: ds.borderWidth ?? 2.2, pointBackgroundColor: "#fff", pointBorderColor: c, pointBorderWidth: 2 };
          return { ...ds, type: dt, label: ds.label || `${dsLabel} ${i + 1}`, data: ds.data || [], backgroundColor: `${c}CC`, borderColor: c, borderRadius: ds.borderRadius ?? 6, borderSkipped: false, maxBarThickness: 26, stack: ds.stack ?? (isStacked ? "s0" : undefined) };
        }),
      };
    }
    const pc = resolveColor(0);
    if (normalizedChartType === "bar" || widget?.expected_shape === "comparison" || widget?.expected_shape === "category_ranking") {
      return { labels, datasets: [{ label: dsLabel, data: values, borderRadius: 6, borderSkipped: false, backgroundColor: `${pc}CC`, borderColor: pc, maxBarThickness: 28 }] };
    }
    return { labels, datasets: [{ label: dsLabel, data: values, fill: isArea ? "origin" : true, tension: 0.38, borderColor: pc, backgroundColor: isArea ? getAreaFill(pc) : palette.area, pointRadius: 3, pointHoverRadius: 6, pointBackgroundColor: "#fff", pointBorderColor: pc, pointBorderWidth: 2 }] };
  }, [colorOverrides, dsLabel, isArea, isBubble, isDoughnut, isDualAxis, isScatter, isStacked, labels, meta, normalizedChartType, palette, rawDs, resolveColor, secondaryKeys, seriesObj, values, widget]);

  const options = useMemo(() => {
    const hasSecAxis = (chartData.datasets || []).some((d) => d.yAxisID === "y1");
    const linX = isScatter || isBubble;

    // ── Shared helpers ────────────────────────────────────────────────────────
    const fmtLabel = (value) => {
      if (value === null || value === undefined) return "";
      const n = Number(value);
      if (!Number.isFinite(n)) return String(value);
      if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
      if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
      return n % 1 === 0 ? String(n) : n.toFixed(1);
    };

    // Pull the vibrant series color from each dataset — used as pill text color
    const seriesColor = (ctx) => {
      const bc = ctx.dataset?.borderColor;
      return typeof bc === "string" ? bc : SERIES_COLORS[ctx.datasetIndex % SERIES_COLORS.length];
    };

    // Shared white-pill style used by bar and line — legible on any bg, theme-safe
    const pillStyle = {
      backgroundColor: "rgba(255,255,255,0.92)",
      borderWidth: 1,
      borderRadius: 4,
      padding: { top: 3, bottom: 3, left: 7, right: 7 },
      font: { size: 11, weight: "600", family: "Inter, sans-serif" },
      // tint the pill border with the series color for a polished look
      borderColor: (ctx) => {
        const bc = ctx.dataset?.borderColor;
        return typeof bc === "string" ? `${bc}45` : "rgba(0,0,0,0.10)";
      },
    };

    // ── Doughnut labels — percentage inside each slice ─────────────────────
    const doughnutLabelConfig = {
      display: "auto",          // auto-hides when slices are too small
      formatter: (value, ctx) => {
        const data = ctx.chart.data.datasets[0]?.data || [];
        const total = data.reduce((s, v) => s + Number(v || 0), 0);
        if (!total) return "";
        const pct = Math.round((Number(value) / total) * 100);
        return pct < 5 ? "" : `${pct}%`;   // suppress tiny-slice labels
      },
      color: "#ffffff",
      textShadowColor: "rgba(0,0,0,0.40)",
      textShadowBlur: 4,
      font: { size: 12, weight: "700", family: "Inter, sans-serif" },
      anchor: "center",
      align: "center",
      clamp: true,
    };

    // ── Bar labels — floats just above each bar ────────────────────────────
    const barLabelConfig = {
      ...pillStyle,
      display: "auto",          // suppresses colliding labels automatically
      formatter: fmtLabel,
      color: seriesColor,
      anchor: "end",
      align: "end",             // positions label above the bar tip
      offset: 4,
      clamp: true,
      clip: false,
    };

    // ── Line labels — sits above each data point ───────────────────────────
    const lineLabelConfig = {
      ...pillStyle,
      display: "auto",
      formatter: fmtLabel,
      color: seriesColor,
      anchor: "center",
      align: "top",             // clean gap above the point dot
      offset: 8,
      clamp: true,
      clip: false,
    };

    const activeLabelConfig = isDoughnut
      ? doughnutLabelConfig
      : normalizedChartType === "bar"
      ? barLabelConfig
      : lineLabelConfig;

    return {
      responsive: true, maintainAspectRatio: false, cutout: isDoughnut ? "65%" : undefined,
      interaction: { mode: isDoughnut ? "nearest" : "index", intersect: false },
      // extra top room so labels above bars/points never get clipped
      layout: { padding: { top: showDataLabels && !isDoughnut ? 28 : 6, left: 4, right: 4, bottom: 4 } },
      plugins: {
        legend: {
          display: isDoughnut || (chartData.datasets || []).length > 1,
          position: "bottom",
          labels: { color: palette.textMuted, usePointStyle: true, boxWidth: 8, padding: 14, font: { size: 11, family: "Inter, sans-serif" } },
        },
        tooltip: { backgroundColor: palette.surface, titleColor: palette.text, bodyColor: palette.textMuted, borderColor: palette.border, borderWidth: 1, padding: 12, cornerRadius: 10, titleFont: { weight: "600", size: 12 }, bodyFont: { size: 12 } },
        zoom: isDoughnut ? undefined : {
            pan:  { enabled: false },
            zoom: {
              drag: { enabled: true, backgroundColor: "rgba(59,130,246,0.12)", borderColor: "rgba(59,130,246,0.5)", borderWidth: 1 },
              wheel: { enabled: false },
              pinch: { enabled: false },
              mode: "x",
            },
          },
        datalabels: showDataLabels ? activeLabelConfig : { display: false },
      },
      scales: isDoughnut ? {} : {
        x: { stacked: isStacked, type: linX ? "linear" : "category", grid: { display: false }, border: { display: false }, ticks: { color: palette.textMuted, font: { size: 11 }, maxRotation: 30 }, title: meta?.x_label ? { display: true, text: meta.x_label, color: palette.textMuted, font: { size: 11, weight: "600" } } : undefined },
        y: { beginAtZero: true, stacked: isStacked && !hasSecAxis, grid: { color: palette.grid, drawBorder: false }, border: { display: false }, ticks: { color: palette.textMuted, font: { size: 11 } }, title: meta?.y_label ? { display: true, text: meta.y_label, color: palette.textMuted, font: { size: 11, weight: "600" } } : undefined },
        ...(hasSecAxis ? { y1: { beginAtZero: true, position: "right", grid: { drawOnChartArea: false }, border: { display: false }, ticks: { color: palette.textMuted, font: { size: 11 } } } } : {}),
      },
    };
  }, [chartData.datasets, isBubble, isDoughnut, isScatter, isStacked, meta, normalizedChartType, palette, showDataLabels]);

  if (isDoughnut) return <Doughnut ref={chartRef} data={chartData} options={options} />;
  if (isBubble) return <Bubble ref={chartRef} data={chartData} options={options} />;
  if (isScatter) return <Scatter ref={chartRef} data={chartData} options={options} />;
  if (normalizedChartType === "bar") return <Bar ref={chartRef} data={chartData} options={options} />;
  return <Line ref={chartRef} data={chartData} options={options} key={`${theme}-${chartTypeOverride}`} />;
};

// ─── Collapsible editor section ──────────────────────────────────────────────

const EditorSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`da-editor-accordion${open ? " is-open" : ""}`}>
      <button type="button" className="da-editor-accordion__header" onClick={() => setOpen((p) => !p)}>
        <span className="da-editor-accordion__title">{title}</span>
        <span className="da-editor-accordion__chevron">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="da-editor-accordion__body">{children}</div>}
    </div>
  );
};

// ─── Right-side sliding Chart Editor ─────────────────────────────────────────

const ChartEditorSidebar = ({ widget, editState, onChange, onClose }) => {
  const { chartTypeOverride, colorOverrides, editedLabels, editedValues, showDataLabels } = editState;
  const cdi = widget?.chart_data || {};
  const shape = widget?.expected_shape || "default";
  const compatibleTypes = COMPATIBLE_CHART_TYPES[shape] || COMPATIBLE_CHART_TYPES.default;

  const activeType = chartTypeOverride || (
    cdi?.chart_type === "histogram" ? "bar" :
    cdi?.chart_type === "pie" ? "doughnut" :
    cdi?.chart_type || "line"
  );
  const isPieLike = activeType === "doughnut" || activeType === "pie" || (!chartTypeOverride && shape === "distribution");

  const originalLabels = cdi?.labels || [];
  const originalValues = cdi?.values || cdi?.datasets?.[0]?.data || [];
  const hasSimpleData = originalLabels.length > 0 && originalValues.length > 0;
  const wl = editedLabels || originalLabels;
  const wv = editedValues || originalValues;
  const colorSlotLabels = useMemo(() => {
    if (isPieLike) return wl.length ? wl : (cdi?.labels || []);
    if (cdi?.series && typeof cdi.series === "object" && !Array.isArray(cdi.series)) return Object.keys(cdi.series);
    if (Array.isArray(cdi?.datasets) && cdi.datasets.length) return cdi.datasets.map((ds, i) => ds?.label || `Series ${i + 1}`);
    return [cdi?.meta?.y_label || widget?.title || "Series 1"];
  }, [cdi?.datasets, cdi?.labels, cdi?.meta?.y_label, cdi?.series, isPieLike, widget?.title, wl]);
  const colorSlotCount = Math.max(colorSlotLabels.length, isPieLike ? wv.length : 0, 1);

  const updateLabel = (i, v) => { const n = [...wl]; n[i] = v; onChange({ ...editState, editedLabels: n }); };
  const updateValue = (i, v) => { const n = [...wv]; n[i] = isNaN(Number(v)) ? v : Number(v); onChange({ ...editState, editedValues: n }); };
  const addRow = () => onChange({ ...editState, editedLabels: [...wl, `Item ${wl.length + 1}`], editedValues: [...wv, 0] });
  const deleteRow = (i) => { const nl = wl.filter((_, j) => j !== i); const nv = wv.filter((_, j) => j !== i); onChange({ ...editState, editedLabels: nl.length ? nl : null, editedValues: nv.length ? nv : null }); };
  const updateColor = (i, v) => {
    const nextColors = Array.from({ length: colorSlotCount }, (_, idx) => colorOverrides?.[idx] || getIndexedSeriesColor(idx));
    nextColors[i] = v;
    onChange({ ...editState, colorOverrides: nextColors });
  };

  return (
    <div className="da-editor-sidebar">
      <div className="da-editor-sidebar__header">
        <div className="da-editor-sidebar__title">
          <Settings2 size={16} />
          <span>Chart Settings</span>
        </div>
        <div className="da-editor-sidebar__header-actions">
          <button type="button" className="da-editor-sidebar__reset" onClick={() => onChange(DEFAULT_EDIT_STATE)} title="Reset all">
            <RotateCcw size={13} /> Reset
          </button>
          <button type="button" className="da-editor-sidebar__close" onClick={onClose}><X size={15} /></button>
        </div>
      </div>

      <div className="da-editor-sidebar__widget-name">{widget.title}</div>

      <div className="da-editor-sidebar__body da-editor-sidebar__body--stacked">

        {/* ── Section: Chart Type ── */}
        <EditorSection title="Chart Type" defaultOpen>
          <div className="da-chart-type-grid">
            {compatibleTypes.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                className={`da-chart-type-tile${activeType === value ? " is-active" : ""}`}
                onClick={() => onChange({ ...editState, chartTypeOverride: value === activeType && !chartTypeOverride ? null : value })}
              >
                <Icon size={20} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </EditorSection>

        {/* ── Section: Colors ── */}
        <EditorSection title="Series Colors" defaultOpen>
          <div className="da-color-swatches">
            {Array.from({ length: colorSlotCount }, (_, i) => {
              const cur = colorOverrides?.[i] || getIndexedSeriesColor(i);
              const swatchLabel = colorSlotLabels[i] || `${isPieLike ? "Category" : "Series"} ${i + 1}`;
              const swatchText = swatchLabel.length > 10 ? `${swatchLabel.slice(0, 9)}…` : swatchLabel;
              return (
                <label key={i} className="da-color-swatch-label" title={swatchLabel}>
                  <input type="color" value={cur} onChange={(e) => updateColor(i, e.target.value)} />
                  <span className="da-color-swatch" style={{ background: cur }} />
                  <span className="da-color-swatch-text">{swatchText}</span>
                </label>
              );
            })}
          </div>
        </EditorSection>

        {/* ── Section: Display Options ── */}
        <EditorSection title="Display Options" defaultOpen>
          <div className="da-display-options">
            <label className="da-display-toggle">
              <div className="da-display-toggle__info">
                <span className="da-display-toggle__label">Show Data Labels</span>
                <span className="da-display-toggle__desc">Display values directly on chart points and bars</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!showDataLabels}
                className={`da-toggle-switch${showDataLabels ? " is-on" : ""}`}
                onClick={() => onChange({ ...editState, showDataLabels: !showDataLabels })}
              >
                <span className="da-toggle-switch__thumb" />
              </button>
            </label>
          </div>
        </EditorSection>

        {/* ── Section: Data ── */}
        {hasSimpleData && (
          <EditorSection title={`Data  ·  ${wl.length} pts`}>
            <div className="da-data-editor-header">
              <button type="button" className="da-data-add-btn" onClick={addRow}><Plus size={12} /> Add row</button>
            </div>
            <div className="da-data-editor-table">
              <div className="da-data-editor-table__head">
                <span>Label</span><span>Value</span><span></span>
              </div>
              <div className="da-data-editor-table__body">
                {wl.map((lbl, i) => (
                  <div key={i} className="da-data-editor-table__row">
                    <input className="da-data-input" value={lbl} onChange={(e) => updateLabel(i, e.target.value)} placeholder="Label" />
                    <input className="da-data-input da-data-input--num" type="number" value={wv[i] ?? ""} onChange={(e) => updateValue(i, e.target.value)} placeholder="0" />
                    <button type="button" className="da-data-delete-btn" onClick={() => deleteRow(i)} disabled={wl.length <= 1}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          </EditorSection>
        )}
      </div>
    </div>
  );
};

// ─── Widget Card ──────────────────────────────────────────────────────────────

const WidgetCard = ({ widget, accent, palette, theme, planItem, onEdit, activeEditId, editState, fullHeight, isFullTable }) => {
  const Icon = widgetIconByShape[widget.expected_shape] || BarChart3;
  const isTable = widget.expected_shape === "detail_table";
  const isEditing = activeEditId === widget.widget_id;
  const hasEdits = editState && Object.values(editState).some((v) => v !== null);
  const chartResetFnRef = useRef(null);
  const handleResetRef = useCallback((fn) => { chartResetFnRef.current = fn; }, []);

  return (
    <div
      className={`da-widget-card${isEditing ? " editor-open" : ""}${fullHeight ? " da-widget-card--scatter" : ""}${isFullTable ? " da-widget-card--table" : ""}`}
      style={{ "--wa": accent.color, "--wa-soft": accent.soft, "--wspan": planItem.span, "--wspan-md": planItem.spanMd }}
    >
      {/* Card itself */}
      <div className="da-widget-card__inner">
        <div className="da-widget-card__header">
          <div className="da-widget-card__header-left">
            <span className="da-widget-card__icon"><Icon size={15} /></span>
            <h3 className="da-widget-card__title">{widget.title}</h3>
          </div>
          <div className="da-widget-card__header-right">
            {isTable && (
              <button
                type="button"
                className="da-widget-action-btn"
                title="Download CSV"
                onClick={() => downloadTableCSV(widget.table_data, widget.title)}
              >
                <Download size={14} />
                <span>CSV</span>
              </button>
            )}
            {!isTable && (
              <>
                <button
                  type="button"
                  className="da-widget-action-btn da-widget-action-btn--reset"
                  title="Reset zoom"
                  onClick={() => chartResetFnRef.current?.()}
                >
                  <RotateCcw size={13} />
                </button>
                <button
                  type="button"
                  className={`da-widget-action-btn${isEditing ? " is-active" : ""}${hasEdits && !isEditing ? " has-edits" : ""}`}
                  title={isEditing ? "Close settings" : "Chart settings"}
                  onClick={() => onEdit(widget)}
                >
                  <Palette size={14} />
                  <span>Edit</span>
                  {hasEdits && !isEditing && <span className="da-edit-dot" />}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="da-widget-card__canvas">
          {isTable ? (
            <div className="da-table-wrap">
              <table className="da-table">
                <thead>
                  <tr>{Object.keys(widget.table_data?.[0] || {}).map((c) => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {(widget.table_data || []).map((row, i) => (
                    <tr key={`${widget.widget_id}_${i}`}>
                      {Object.entries(row).map(([c, v]) => <td key={`${c}_${i}`}>{String(v)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <DashboardChart
              widget={widget} palette={palette} theme={theme}
              chartTypeOverride={editState?.chartTypeOverride}
              colorOverrides={editState?.colorOverrides}
              editedLabels={editState?.editedLabels}
              editedValues={editState?.editedValues}
              showDataLabels={editState?.showDataLabels ?? false}
              onResetRef={handleResetRef}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const DashboardSkeleton = () => (
  <div className="da-dashboard">
    <div className="da-dashboard__meta">
      <div><SkeletonLoader width={160} height={13} /><SkeletonLoader width={320} height={28} className="mt-3" /></div>
      <SkeletonLoader width={140} height={32} />
    </div>
    <div className="da-kpi-grid">
      {[0,1,2,3,4].map((i) => <div key={i} className="da-kpi-card da-kpi-card--skeleton"><SkeletonLoader width={100} height={13} /><SkeletonLoader width={80} height={28} className="mt-3" /><SkeletonLoader width={120} height={13} className="mt-3" /></div>)}
    </div>
    <div className="da-widget-grid">
      {[0,1,2,3].map((i) => (
        <div key={i} className="da-widget-card" style={{ "--wspan": 6 }}>
          <div className="da-widget-card__inner">
            <SkeletonLoader width={160} height={16} /><SkeletonLoader width="100%" height={220} className="mt-4" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Export Toolbar ───────────────────────────────────────────────────────────

// const ExportToolbar = ({ displayDashboard, metadata }) => (
//   <div className="da-export-toolbar">
//     <button
//       type="button"
//       className="da-export-btn da-export-btn--powerbi"
//       onClick={() => exportDashboard("powerbi", displayDashboard, metadata)}
//       title="Export to Power BI"
//     >
//       <Download size={14} />
//       <span>Power BI</span>
//     </button>
//     <button
//       type="button"
//       className="da-export-btn da-export-btn--tableau"
//       onClick={() => exportDashboard("tableau", displayDashboard, metadata)}
//       title="Export to Tableau"
//     >
//       <Download size={14} />
//       <span>Tableau</span>
//     </button>
//   </div>
// );

const ExportToolbar = ({ onOpen }) => (
  <div className="da-export-toolbar">
    <button
      type="button"
      className="da-export-btn da-export-btn--export"
      onClick={onOpen}
      title="Export to Power BI or Tableau"
    >
      <Download size={14} />
      <span>Export</span>
    </button>
  </div>
);
// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DashboardAgentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const {
    databases = [],
    selectedDatabaseId,
    storageScope,
    onSelectDatabase,
  } = useOutletContext() || {};

  // Live-ticking relative timestamp for the last refresh/generation time
  const [relativeTime, setRelativeTime] = useState("Just now");
  const refreshTimeRef = useRef(null);

  const [prompt, setPrompt] = useState("");
  const [activeResponse, setActiveResponse] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingDashboardId, setLoadingDashboardId] = useState(null);
  const [error, setError] = useState("");
  const [voiceLang, setVoiceLang] = useState(
    () => readScopedValue(storageScope, "voice_lang") || "en-IN"
  );
  const [listening, setListening] = useState(false);
  const [isPromptEditing, setIsPromptEditing] = useState(false);
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const lastLoadedDashboardKeyRef = useRef(null);
  const lastResetKeyRef = useRef(null);
  const activeResponseRef = useRef(null);
  const previousSelectedDatabaseIdRef = useRef(selectedDatabaseId ?? "");
  const suppressNextDatabaseResetRef = useRef(false);

  useEffect(() => {
    writeScopedValue(storageScope, "voice_lang", voiceLang);
  }, [storageScope, voiceLang]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop?.();
    };
  }, []);

  // Global chart editor panel state
  const [editPanelWidget, setEditPanelWidget] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [widgetEditStates, setWidgetEditStates] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);

  const handleOpenEditPanel = useCallback((widget) => {
    setEditPanelWidget((prev) => prev?.widget_id === widget.widget_id ? null : widget);
  }, []);

  const handleEditStateChange = useCallback((widgetId, newState) => {
    setWidgetEditStates((prev) => ({ ...prev, [widgetId]: newState }));
  }, []);

  const handleCloseEditPanel = useCallback(() => {
    setEditPanelWidget(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!activeResponse || isRefreshing) return;
    setIsRefreshing(true);
    setError("");
    try {
      const savedId = activeResponse?.saved_dashboard_id;

      if (savedId) {
        // ✅ TRUE refresh: re-runs queries against live DB and saves snapshot
        const result = await refreshSavedDashboard(savedId);
        const sd = result?.saved_dashboard;
        if (sd) {
          setActiveResponse(normalizeSavedDashboard(sd));
          toast.success("Dashboard refreshed with latest data.");
        }
      } else if (prompt.trim() && selectedDatabaseId) {
        // Fallback for unsaved dashboards: re-generate
        const resp = await generateDashboard({
          question: prompt.trim(),
          source_db_config_id: Number(selectedDatabaseId),
        });
        setActiveResponse(resp);
        toast.success("Dashboard regenerated.");
      } else {
        toast.warn("Save the dashboard first to enable data refresh.");
      }

      setRefreshedAt(new Date().toISOString());
      setRelativeTime("Just now"); 
    } catch (e) {
      toast.error("Refresh failed: " + (e.message || "Unknown error"));
    } finally {
      setIsRefreshing(false);
    }
  }, [activeResponse, isRefreshing, prompt, selectedDatabaseId]);
  const lastLoadedKeyRef = useRef(null);
  const prevDbIdRef = useRef(selectedDatabaseId ?? "");
  const suppressResetRef = useRef(false);

  useEffect(() => { activeResponseRef.current = activeResponse; }, [activeResponse]);

  useEffect(() => {
    const ta = textareaRef.current; if (!ta) return;
    ta.style.height = "auto";
    if (!isPromptEditing) { ta.style.height = "32px"; ta.style.overflowY = "hidden"; return; }
    const next = Math.min(ta.scrollHeight, 96);
    ta.style.height = `${Math.max(32, next)}px`; ta.style.overflowY = ta.scrollHeight > 96 ? "auto" : "hidden";
  }, [isPromptEditing, prompt]);

  const palette = useMemo(() => ({
    primary: readCssVar("--primary", "#3B82F6"),
    secondary: readCssVar("--secondary", "#8B5CF6"),
    border: readCssVar("--border", "rgba(148,163,184,0.16)"),
    text: readCssVar("--foreground", "#0f172a"),
    textMuted: readCssVar("--muted-foreground", "#64748b"),
    surface: readCssVar("--surface", "#ffffff"),
    grid: theme === "dark" ? "rgba(148,163,184,0.1)" : "rgba(226,232,240,0.8)",
    area: theme === "dark" ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.1)",
    series: SERIES_COLORS,
  }), [theme]);

  const clearDashboardState = useCallback(({ clearPrompt = false } = {}) => {
    setActiveResponse(null); setError(""); if (clearPrompt) setPrompt("");
  }, []);

  useEffect(() => {
    const prev = prevDbIdRef.current ?? ""; const next = selectedDatabaseId ?? "";
    if (String(prev) === String(next)) return;
    prevDbIdRef.current = next;
    if (suppressResetRef.current) { suppressResetRef.current = false; return; }
    if (activeResponseRef.current?.saved_dashboard_id === DEMO_DASHBOARD_ID) return;
    setActiveResponse(null); setError(""); lastLoadedKeyRef.current = null; lastResetKeyRef.current = null;
  }, [selectedDatabaseId]);

  const translateToEnglish = useCallback(async (text, lang) => {
    if (!lang || lang.startsWith("en")) return text;
    try {
      const r = await fetch("https://libretranslate.com/translate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ q: text, source: lang.split("-")[0], target: "en", format: "text" }) });
      return (await r.json())?.translatedText || text;
    } catch { return text; }
  }, []);

  const handleVoiceInput = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input not supported in this browser."); return; }
    if (listening && recognitionRef.current) { recognitionRef.current.stop(); return; }
    const rec = new SR(); recognitionRef.current = rec;
    rec.lang = voiceLang; rec.interimResults = false; rec.maxAlternatives = 1;
    let ft = "";
    rec.onstart = () => setListening(true);
    rec.onresult = (e) => { ft = e.results?.[0]?.[0]?.transcript || ""; };
    rec.onerror = (e) => { console.error(e.error); toast.error("Voice recognition failed."); setListening(false); };
    rec.onend = async () => {
      setListening(false); recognitionRef.current = null;
      const cleaned = ft.trim(); if (!cleaned) return;
      const en = await translateToEnglish(cleaned, voiceLang);
      setPrompt((p) => p.trim() ? `${p.trim()} ${en}` : en);
    };
    rec.start();
  }, [listening, translateToEnglish, voiceLang]);

  const handleGenerate = async () => {
    if (!selectedDatabaseId || !prompt.trim() || listening) return;
    setIsGenerating(true); setError(""); setIsPromptEditing(false);
    try {
      const resp = await generateDashboard({ question: prompt.trim(), source_db_config_id: Number(selectedDatabaseId) });
      setActiveResponse(resp);
      if (resp?.save_error) toast.warn(resp.save_error);
      window.dispatchEvent(new CustomEvent("dashboard-recents-updated", { detail: { sourceDbId: Number(selectedDatabaseId) } }));
      navigate("/dashboard", { replace: true });
    } catch (e) { setError(getLlmProviderErrorMessage(e) || e.message || "Unable to generate dashboard."); setActiveResponse(null); }
    finally { setIsGenerating(false); }
  };

  const handleOpenDashboard = useCallback(async (id) => {
    if (!selectedDatabaseId && !isDemoDashboardId(id)) return;
    setLoadingDashboardId(id); setError("");
    try {
      const r = await getSavedDashboard(id); const sd = r?.saved_dashboard;
      if (!sd) throw new Error("Saved dashboard not found.");
      const nid = sd?.source_db_config_id ?? "";
      if (!isDemoDashboardId(id) && nid !== "" && nid !== null && String(nid) !== String(selectedDatabaseId)) {
        suppressResetRef.current = true; onSelectDatabase?.(String(nid));
      }
      setActiveResponse(normalizeSavedDashboard(sd));
      if (sd?.question) setPrompt(sd.question);
    } catch (e) { setError(e.message || "Unable to load dashboard."); }
    finally { setLoadingDashboardId(null); }
  }, [onSelectDatabase, selectedDatabaseId]);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedId = searchParams.get("dashboardId");
  const newToken = searchParams.get("new");

  useEffect(() => {
    if (requestedId) {
      if (!selectedDatabaseId && !isDemoDashboardId(requestedId)) return;
      const k = `dashboard:${requestedId}`;
      if (lastLoadedKeyRef.current === k) return;
      lastLoadedKeyRef.current = k; handleOpenDashboard(requestedId); return;
    }
    if (newToken) {
      if (lastResetKeyRef.current === newToken) return;
      lastResetKeyRef.current = newToken; lastLoadedKeyRef.current = null; clearDashboardState({ clearPrompt: true });
    }
  }, [clearDashboardState, handleOpenDashboard, newToken, requestedId, selectedDatabaseId]);

  const displayDashboard = useMemo(() => normalizeDashboardForDisplay(activeResponse?.dashboard), [activeResponse]);
  const layoutPlan = useMemo(() => buildDashboardLayoutPlan(displayDashboard), [displayDashboard]);
  const { orderedWidgets, kpiPlan: kpiLayout, analysisPlan: analysisLayout } = layoutPlan;

  const metadata = displayDashboard?.metadata || {};
  useEffect(() => {
    const rawTime = refreshedAt || metadata.generated_at || activeResponse?.updated_at || activeResponse?.created_at;
    refreshTimeRef.current = rawTime;
    setRelativeTime(formatRelativeTime(rawTime));

    const interval = setInterval(() => {
      setRelativeTime(formatRelativeTime(refreshTimeRef.current));
    }, 30_000); // update every 30 seconds

    return () => clearInterval(interval);
  }, [refreshedAt, activeResponse, metadata.generated_at]);
  const canGenerate = Boolean(selectedDatabaseId && prompt.trim() && !isGenerating && !listening);
  // metadataTime is now driven by the live `relativeTime` state (see useEffect above)
  const tfl = metadata.timeframe_label || orderedWidgets.find((w) => w.timeframe_label)?.timeframe_label || "";
  const timeframeBadge = formatTimeframeBadge({ timeframeLabel: tfl, timeframeStart: metadata.timeframe_start || orderedWidgets.find((w) => w.timeframe_start)?.timeframe_start, timeframeEnd: metadata.timeframe_end || orderedWidgets.find((w) => w.timeframe_end)?.timeframe_end });
  const isDemoActive = activeResponse?.saved_dashboard_id === DEMO_DASHBOARD_ID || isDemoDashboardId(requestedId);
  const showDbEmpty = !databases.length && !isDemoActive;

  return (
    <div className={`da-page page-scroll no-scrollbar${editPanelWidget ? " da-page--panel-open" : ""}`}>
      <div className="da-layout-frame">
      <div className="da-content-area">
      <div className="da-shell">

        {/* ── Prompt bar ── */}
        <section className="da-prompt-bar">
          <div className="da-prompt-bar__label">AI Dashboard Builder</div>
          <div className="da-prompt-bar__row">
            <div className="da-prompt-bar__input-wrap">
              <Textarea
                ref={textareaRef}
                className="da-prompt-input"
                rows={1}
                value={prompt}
                readOnly={listening}
                onFocus={() => setIsPromptEditing(true)}
                onBlur={() => setIsPromptEditing(false)}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Build an executive sales dashboard with KPI cards, monthly trend, regional ranking and deal table…"
              />
              <div className="da-prompt-bar__actions">
                <label className="da-voice-lang-wrap">
                  <Globe size={14} />
                  <select className="da-voice-lang-select" value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)} disabled={listening || isGenerating}>
                    {VOICE_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.shortLabel}</option>)}
                  </select>
                </label>
                <button type="button" className={`da-voice-btn${listening ? " listening" : ""}`} onClick={handleVoiceInput} disabled={isGenerating} title={listening ? "Stop" : "Voice input"}>
                  <Mic size={16} />
                </button>
                <Button onClick={handleGenerate} disabled={!canGenerate} className="da-generate-btn">
                  {isGenerating ? <RefreshCcw size={15} className="animate-spin" /> : <LayoutDashboard size={15} />}
                  {isGenerating ? "Generating…" : "Generate"}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── States ── */}
        {showDbEmpty && (
          <Card className="da-empty-card">
            <EmptyState icon={Database} title="No database connected" description="Connect a database in Settings to generate dashboards." action={<Button onClick={() => navigate("/settings")}>Open Settings</Button>} />
          </Card>
        )}
        {error && <div className="da-error-banner"><AlertCircle size={16} /><span>{error}</span></div>}
        {(isGenerating || loadingDashboardId) && <DashboardSkeleton />}

        {/* ── Dashboard ── */}
        {!isGenerating && !loadingDashboardId && activeResponse && (
          <div className="da-dashboard">

            {/* Header */}
            <div className="da-dashboard__header">
              <div className="da-dashboard__header-left">
                <span className="da-dashboard__eyebrow">Generated Dashboard</span>
                <h2 className="da-dashboard__title">{metadata.title || "Dashboard Preview"}</h2>
              </div>
              <div className="da-dashboard__header-right">
                <div className="da-dashboard__meta-pills">
                  <span
                    className="da-meta-pill"
                    title={formatTimestamp(refreshedAt || metadata.generated_at || activeResponse?.updated_at || activeResponse?.created_at)}
                  >
                    <Clock3 size={13} />
                    {relativeTime}
                  </span>
                                    {timeframeBadge && <span className="da-meta-pill da-meta-pill--brand">{timeframeBadge}</span>}
                  <button
                    type="button"
                    className={`da-refresh-btn${isRefreshing ? " is-spinning" : ""}`}
                    title={
                      isRefreshing
                        ? "Refreshing…"
                        : refreshedAt
                        ? `Last refreshed: ${formatTimestamp(refreshedAt)}`
                        : "Refresh dashboard data"
                    }
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    <RefreshCcw size={13} />
                  </button>
                </div>
                <ExportToolbar onOpen={() => setShowExportModal(true)} />
              </div>
            </div>

            {/* Filters */}
            {(displayDashboard?.filters || []).length > 0 && (
              <div className="da-filter-bar">
                <span className="da-filter-bar__label"><Filter size={14} /> Filters</span>
                <div className="da-filter-chips">
                  {displayDashboard.filters.map((f) => <span key={f} className="da-filter-chip">{f}</span>)}
                </div>
              </div>
            )}

            {/* KPI Grid */}
            {kpiLayout.length > 0 && (
              <div className="da-kpi-grid">
                {kpiLayout.map((pi, i) => (
                  <KpiCard key={pi.widget.widget_id} widget={pi.widget} planItem={pi} accent={ACCENT_PALETTE[i % ACCENT_PALETTE.length]} />
                ))}
              </div>
            )}

            {/* Chart grid — smart layout */}
            {analysisLayout.length > 0 && (() => {
              const isScatterWidget = (w) => w?.chart_data?.chart_type === "scatter" || w?.chart_data?.chart_type === "bubble" || w?.expected_shape === "scatter";
              const isTableWidget   = (w) => w?.expected_shape === "detail_table" || String(w?.widget_type || "").includes("table");
              const regular = analysisLayout.filter((pi) => !isScatterWidget(pi.widget) && !isTableWidget(pi.widget));
              const scatter = analysisLayout.filter((pi) => isScatterWidget(pi.widget));
              const tables  = analysisLayout.filter((pi) => isTableWidget(pi.widget));
              // accent index offset helpers
              let ai = 0;
              const nextAccent = () => ACCENT_PALETTE[(ai++) % ACCENT_PALETTE.length];
              return (
                <>
                  {/* Regular charts */}
                  {regular.length > 0 && (
                    <div className="da-widget-grid">
                      {regular.map((pi) => {
                        const accent = nextAccent();
                        return (
                          <WidgetCard
                            key={pi.widget.widget_id}
                            widget={pi.widget}
                            planItem={pi}
                            accent={accent}
                            palette={palette}
                            theme={theme}
                            onEdit={handleOpenEditPanel}
                            activeEditId={editPanelWidget?.widget_id}
                            editState={widgetEditStates[pi.widget.widget_id] || DEFAULT_EDIT_STATE}
                          />
                        );
                      })}
                    </div>
                  )}
                  {/* Scatter / bubble — always full-width */}
                  {scatter.map((pi) => {
                    const accent = nextAccent();
                    return (
                      <div key={pi.widget.widget_id} className="da-widget-grid">
                        <WidgetCard
                          widget={pi.widget}
                          planItem={{ ...pi, span: 12, spanMd: 12 }}
                          accent={accent}
                          palette={palette}
                          theme={theme}
                          onEdit={handleOpenEditPanel}
                          activeEditId={editPanelWidget?.widget_id}
                          editState={widgetEditStates[pi.widget.widget_id] || DEFAULT_EDIT_STATE}
                          fullHeight
                        />
                      </div>
                    );
                  })}
                  {/* Tables — always last, full-width */}
                  {tables.length > 0 && (
                    <div className="da-tables-section">
                      {tables.map((pi) => {
                        const accent = nextAccent();
                        return (
                          <WidgetCard
                            key={pi.widget.widget_id}
                            widget={pi.widget}
                            planItem={{ ...pi, span: 12, spanMd: 12 }}
                            accent={accent}
                            palette={palette}
                            theme={theme}
                            onEdit={handleOpenEditPanel}
                            activeEditId={editPanelWidget?.widget_id}
                            editState={widgetEditStates[pi.widget.widget_id] || DEFAULT_EDIT_STATE}
                            isFullTable
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ── Empty canvas ── */}
        {!isGenerating && !loadingDashboardId && !activeResponse && databases.length > 0 && (
          <Card className="da-empty-card">
            <EmptyState icon={LayoutDashboard} title="Your canvas is ready" description="Describe the KPI story you want to explore and click Generate." />
          </Card>
        )}
      </div>
      </div>{/* /da-content-area */}

      {/* ── Global Chart Editor Side Panel ── */}
      {editPanelWidget && (
        <div className="da-global-editor-panel">
          <ChartEditorSidebar
            widget={editPanelWidget}
            editState={widgetEditStates[editPanelWidget.widget_id] || DEFAULT_EDIT_STATE}
            onChange={(newState) => handleEditStateChange(editPanelWidget.widget_id, newState)}
            onClose={handleCloseEditPanel}
          />
        </div>
      )}
      {showExportModal && (
        <ExportModal
          displayDashboard={displayDashboard}
          metadata={metadata}
          onClose={() => setShowExportModal(false)}
        />
      )}
      </div>{/* /da-layout-frame */}
    </div>
  );
}
